import { useEffect, useMemo, useRef } from 'react';
import { Bold, Italic, List, ListOrdered } from 'lucide-react';

type Props = {
  value: string;
  onChange: (nextHtml: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
};

function isEmptyHtml(html: string): boolean {
  const cleaned = html
    .replace(/<br\s*\/?>/gi, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .trim();
  return cleaned.length === 0;
}

export function RichTextEditor({ value, onChange, placeholder, disabled, className }: Props) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (el.innerHTML !== value) el.innerHTML = value || '';
  }, [value]);

  const placeholderVisible = useMemo(() => isEmptyHtml(value), [value]);

  const exec = (command: string, arg?: string) => {
    if (disabled) return;
    ref.current?.focus();
    document.execCommand(command, false, arg);
    onChange(ref.current?.innerHTML || '');
  };

  const addLink = () => {
    if (disabled) return;
    const url = window.prompt('URL (https://, mailto:, tel:, /path, #anchor)');
    if (!url) return;
    exec('createLink', url);
  };

  return (
    <div className={className}>
      <div className="flex flex-wrap items-center gap-1 border border-gray-300 rounded-t-lg bg-gray-50 px-2 py-1 overflow-x-auto">
        <button
          type="button"
          onClick={() => exec('bold')}
          disabled={disabled}
          className="p-2 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          title="Bold"
        >
          <Bold className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => exec('italic')}
          disabled={disabled}
          className="p-2 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          title="Italic"
        >
          <Italic className="h-4 w-4" />
        </button>
        <div className="w-px h-6 bg-gray-200 mx-1" />
        <button
          type="button"
          onClick={() => exec('underline')}
          disabled={disabled}
          className="px-2 py-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
          title="Underline"
        >
          U
        </button>
        <button
          type="button"
          onClick={() => exec('strikeThrough')}
          disabled={disabled}
          className="px-2 py-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
          title="Strikethrough"
        >
          S
        </button>
        <div className="w-px h-6 bg-gray-200 mx-1" />
        <button
          type="button"
          onClick={() => exec('insertUnorderedList')}
          disabled={disabled}
          className="p-2 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          title="Bulleted list"
        >
          <List className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => exec('insertOrderedList')}
          disabled={disabled}
          className="p-2 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          title="Numbered list"
        >
          <ListOrdered className="h-4 w-4" />
        </button>
        <div className="w-px h-6 bg-gray-200 mx-1" />
        <button
          type="button"
          onClick={addLink}
          disabled={disabled}
          className="px-2 py-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium"
          title="Link"
        >
          Link
        </button>
        <button
          type="button"
          onClick={() => exec('unlink')}
          disabled={disabled}
          className="px-2 py-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium"
          title="Unlink"
        >
          Unlink
        </button>
        <div className="w-px h-6 bg-gray-200 mx-1" />
        <button
          type="button"
          onClick={() => exec('undo')}
          disabled={disabled}
          className="px-2 py-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium"
          title="Undo"
        >
          Undo
        </button>
        <button
          type="button"
          onClick={() => exec('redo')}
          disabled={disabled}
          className="px-2 py-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium"
          title="Redo"
        >
          Redo
        </button>
        <button
          type="button"
          onClick={() => exec('removeFormat')}
          disabled={disabled}
          className="px-2 py-1 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium"
          title="Clear formatting"
        >
          Clear
        </button>
      </div>

      <div className="relative border border-t-0 border-gray-300 rounded-b-lg bg-white">
        {placeholderVisible && (
          <div className="pointer-events-none absolute inset-0 px-3 py-2 text-sm text-gray-400">
            {placeholder || 'Add a description (optional)…'}
          </div>
        )}
        <div
          ref={ref}
          contentEditable={!disabled}
          onInput={() => onChange(ref.current?.innerHTML || '')}
          className="min-h-[110px] px-3 py-2 text-sm outline-none max-w-none
            [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-2
            [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:my-2
            [&_li]:my-1
            [&_p]:my-1
          "
          onPaste={(e) => {
            if (disabled) return;
            e.preventDefault();
            const text = e.clipboardData.getData('text/plain');
            document.execCommand('insertText', false, text);
            onChange(ref.current?.innerHTML || '');
          }}
        />
      </div>
    </div>
  );
}


